import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { liberacaoService } from '../services';
import useDebounce from '../hooks/useDebounce';
import ModalAutorizacao from '../components/ModalAutorizacao';
import ModalAutorizacaoSimples from '../components/ModalAutorizacaoSimples';
import { 
  Plus, 
  Search, 
  Eye, 
  AlertTriangle,
  FileText,
  Calendar,
  User,
  Package,
  Filter,
  Download,
  Shield
} from 'lucide-react';

const Liberacoes = () => {
  const { user } = useAuth();
  const [liberacoes, setLiberacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSearching, setIsSearching] = useState(false);
  
  // Aplicar debounce na pesquisa com 500ms de delay
  const debouncedSearch = useDebounce(search, 500);
  const [showModalAutorizacao, setShowModalAutorizacao] = useState(false);
  const [showModalAutorizacaoSimples, setShowModalAutorizacaoSimples] = useState(false);
  const [liberacaoParaAutorizar, setLiberacaoParaAutorizar] = useState(null);
  const [loadingAutorizacao, setLoadingAutorizacao] = useState(false);

  const carregarLiberacoes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: debouncedSearch,
        status: filtroStatus,
        dataInicio,
        dataFim
      };
      
      // Remover parâmetros vazios
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await liberacaoService.getAll(params);
      setLiberacoes(response.liberacoes || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar liberações:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar liberações' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filtroStatus, dataInicio, dataFim]);

  useEffect(() => {
    carregarLiberacoes();
  }, [carregarLiberacoes]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value !== debouncedSearch) {
      setIsSearching(true);
    }
  };

  // Resetar página quando o debouncedSearch muda
  useEffect(() => {
    setCurrentPage(1);
    setIsSearching(false);
  }, [debouncedSearch]);

  const handleFiltroStatus = (e) => {
    setFiltroStatus(e.target.value);
    setCurrentPage(1);
  };

  const limparFiltros = () => {
    setSearch('');
    setFiltroStatus('');
    setDataInicio('');
    setDataFim('');
    setCurrentPage(1);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarHora = (data) => {
    return new Date(data).toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { color: '#ffc107', text: 'Pendente' },
      autorizada: { color: '#17a2b8', text: 'Autorizada' },
      confirmada: { color: '#28a745', text: 'Confirmada' },
      cancelada: { color: '#dc3545', text: 'Cancelada' }
    };

    const config = statusConfig[status] || { color: '#6c757d', text: status };
    
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: config.color,
        color: 'white'
      }}>
        {config.text}
      </span>
    );
  };

  const abrirModalAutorizacao = (liberacao) => {
    setLiberacaoParaAutorizar(liberacao);
    setShowModalAutorizacaoSimples(true);
  };

  const fecharModalAutorizacao = () => {
    setShowModalAutorizacao(false);
    setShowModalAutorizacaoSimples(false);
    setLiberacaoParaAutorizar(null);
    setLoadingAutorizacao(false);
  };

  const confirmarAutorizacao = async () => {
    if (!liberacaoParaAutorizar) return;
    
    try {
      setLoadingAutorizacao(true);
      await liberacaoService.autorizar(liberacaoParaAutorizar.id);
      
      setMessage({ 
        type: 'success', 
        text: `Liberação ${liberacaoParaAutorizar.numeroRecibo} autorizada e confirmada com sucesso!` 
      });
      
      // Recarregar a lista
      await carregarLiberacoes();
      
      // Fechar modal
      fecharModalAutorizacao();
      
    } catch (error) {
      // O modal vai lidar com o erro e controle de tentativas
      throw error;
    } finally {
      setLoadingAutorizacao(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={32} />
            Liberações
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Histórico de liberações de medicamentos
          </p>
        </div>
        
        <Link to="/liberacoes/nova" className="btn btn-primary">
          <Plus size={20} />
          Nova Liberação
        </Link>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      <div className="card">
        {/* Filtros */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Search size={16} color={isSearching ? '#007bff' : '#667'} />
              Buscar
              {isSearching && <span style={{ color: '#007bff', fontSize: '12px' }}>Buscando...</span>}
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Paciente, CPF ou nº recibo..."
              value={search}
              onChange={handleSearch}
            />
          </div>

          <div>
            <label className="form-label">
              <Filter size={16} />
              Status
            </label>
            <select
              className="form-control"
              value={filtroStatus}
              onChange={handleFiltroStatus}
            >
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="autorizada">Autorizada</option>
              <option value="confirmada">Confirmada</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>

          <div>
            <label className="form-label">
              <Calendar size={16} />
              Data Início
            </label>
            <input
              type="date"
              className="form-control"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label">
              <Calendar size={16} />
              Data Fim
            </label>
            <input
              type="date"
              className="form-control"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'end' }}>
            <button 
              onClick={limparFiltros}
              className="btn btn-secondary"
              style={{ width: '100%', height: '60%', justifyContent: 'center' }}
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Tabela */}
        {liberacoes.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nº Recibo</th>
                    <th>Data</th>
                    <th>Paciente</th>
                    <th>Responsável</th>
                    <th>Itens</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {liberacoes.map((liberacao) => (
                    <tr key={liberacao.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={16} color="#667" />
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {liberacao.numeroRecibo}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>{formatarData(liberacao.dataLiberacao)}</div>
                          <div style={{ fontSize: '12px', color: '#667' }}>
                            {formatarHora(liberacao.dataLiberacao)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <User size={16} color="#667" />
                          <div>
                            <div style={{ fontWeight: '500' }}>
                              {liberacao.paciente?.nome}
                            </div>
                            <div style={{ fontSize: '12px', color: '#667' }}>
                              CPF: {liberacao.paciente?.cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {liberacao.usuario?.nome}
                          </div>
                          <div style={{ fontSize: '12px', color: '#667' }}>
                            {liberacao.usuario?.cargo}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Package size={16} color="#667" />
                          <span style={{ fontWeight: 'bold' }}>
                            {liberacao.totalItens}
                          </span>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(liberacao.status)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Link 
                            to={`/liberacoes/detalhes/${liberacao.id}`}
                            className="btn btn-secondary"
                            style={{ padding: '6px 10px' }}
                            title="Ver Detalhes"
                          >
                            <Eye size={16} />
                          </Link>
                          
                          {liberacao.status === 'pendente' && ['admin', 'farmaceutico'].includes(user?.cargo) && (
                            <button
                              onClick={() => abrirModalAutorizacao(liberacao)}
                              className="btn btn-success"
                              style={{ padding: '6px 10px' }}
                              title="Autorizar Liberação"
                            >
                              <Shield size={16} />
                            </button>
                          )}
                          
                          {liberacao.status === 'confirmada' && (
                            <Link 
                              to={`/liberacoes/recibo/${liberacao.id}`}
                              className="btn btn-primary"
                              style={{ padding: '6px 10px' }}
                              title="Baixar Recibo"
                            >
                              <Download size={16} />
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <span style={{ margin: '0 20px', color: '#667' }}>
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#667'
          }}>
            <FileText size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
            <h3>Nenhuma liberação encontrada</h3>
            <p>
              {search || filtroStatus || dataInicio || dataFim
                ? 'Tente ajustar os filtros de busca' 
                : 'Ainda não há liberações registradas'
              }
            </p>
            {!search && !filtroStatus && !dataInicio && !dataFim && (
              <Link to="/liberacoes/nova" className="btn btn-primary" style={{ marginTop: '15px' }}>
                <Plus size={20} />
                Criar Primeira Liberação
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Modal de Autorização */}
      <ModalAutorizacao
        isOpen={showModalAutorizacao}
        onClose={fecharModalAutorizacao}
        onConfirm={confirmarAutorizacao}
        loading={loadingAutorizacao}
        liberacao={liberacaoParaAutorizar}
      />

      {/* Modal de Autorização Simples */}
      <ModalAutorizacaoSimples
        isOpen={showModalAutorizacaoSimples}
        onClose={fecharModalAutorizacao}
        onConfirm={confirmarAutorizacao}
        loading={loadingAutorizacao}
        liberacao={liberacaoParaAutorizar}
      />
    </div>
  );
};

export default Liberacoes;